import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { RetentionCurveResponse } from '../services/cohortAnalyticsService';

interface RetentionCurveChartProps {
  data: RetentionCurveResponse;
}

const RetentionCurveChart: React.FC<RetentionCurveChartProps> = ({ data }) => {
  // Transform data for Recharts
  const chartData = data.curve.map(point => ({
    period: point.period,
    retention: point.avgRetentionRate,
    cohorts: point.cohortCount
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 shadow-lg">
        <div className="font-semibold mb-1">Period {data.period}</div>
        <div className="text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span>Retention: {data.retention.toFixed(1)}%</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Based on {data.cohorts} cohort{data.cohorts !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="period"
            label={{ value: 'Period', position: 'insideBottom', offset: -5 }}
            stroke="#6b7280"
          />
          <YAxis
            label={{ value: 'Retention Rate (%)', angle: -90, position: 'insideLeft' }}
            stroke="#6b7280"
            domain={[0, 100]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="retention"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ fill: '#3b82f6', r: 4 }}
            activeDot={{ r: 6 }}
            name="Avg Retention Rate"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RetentionCurveChart;
